import { useMemo, useState } from 'react'
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  MapPin,
  Plus,
  Settings2,
  Stethoscope,
  Users,
  X,
} from 'lucide-react'

type PlanStatus = 'planned' | 'due' | 'completed' | 'overdue'
type CalendarView = 'month' | 'week'

interface MaintenancePlan {
  id: string
  date: string
  time: string
  hospital: string
  location: string
  device: string
  deviceNo: string
  cycle: string
  engineer: string
  status: PlanStatus
}

const hospitals = ['全部医疗机构', '市第一人民医院', '妇幼保健院', '康复医院', '中医院']
const engineers = ['全部负责人', '陈工', '王工', '刘工', '赵工']

const initialPlans: MaintenancePlan[] = [
  ['01', '09:00', '市第一人民医院', '影像科 · CT室', '64排螺旋CT', 'CT-2021-018', '每季度', '陈工', 'completed'],
  ['03', '14:00', '康复医院', '呼吸治疗室', '呼吸机', 'HX-2023-106', '每月', '王工', 'completed'],
  ['06', '10:30', '妇幼保健院', '超声科', '彩色多普勒超声', 'CS-2022-031', '每2个月', '刘工', 'completed'],
  ['08', '09:30', '中医院', '检验科', '全自动生化分析仪', 'SH-2020-079', '每季度', '赵工', 'overdue'],
  ['10', '15:00', '市第一人民医院', '手术室', '麻醉机', 'MZ-2023-022', '每月', '陈工', 'completed'],
  ['12', '08:30', '市第一人民医院', '急诊科抢救室', '除颤监护仪', 'CC-2024-008', '每月', '陈工', 'due'],
  ['12', '10:00', '妇幼保健院', '新生儿科', '婴儿培养箱', 'PY-2022-015', '每2个月', '刘工', 'planned'],
  ['12', '13:30', '康复医院', '重症康复病区', '呼吸机', 'HX-2023-109', '每月', '王工', 'planned'],
  ['12', '15:30', '中医院', '检验科', '血液分析仪', 'XY-2021-063', '每季度', '赵工', 'planned'],
  ['14', '09:00', '市第一人民医院', '影像科 · MRI室', '核磁共振成像系统', 'MRI-2020-006', '每半年', '陈工', 'planned'],
  ['15', '13:00', '康复医院', '康复治疗室', '冲击波治疗仪', 'CJ-2023-017', '每季度', '王工', 'planned'],
  ['16', '09:30', '妇幼保健院', '产房', '胎儿监护仪', 'TJ-2024-026', '每月', '刘工', 'planned'],
  ['16', '14:30', '妇幼保健院', '超声科', '彩色多普勒超声', 'CS-2022-034', '每2个月', '刘工', 'planned'],
  ['20', '10:00', '中医院', '放射科', '数字化X线摄影系统', 'DR-2021-012', '每季度', '赵工', 'planned'],
  ['22', '09:00', '市第一人民医院', 'ICU', '多参数监护仪', 'JH-2024-116', '每月', '陈工', 'planned'],
  ['22', '14:00', '康复医院', '呼吸治疗室', '呼吸机', 'HX-2023-106', '每月', '王工', 'planned'],
  ['27', '09:30', '妇幼保健院', '新生儿科', '黄疸治疗箱', 'HD-2023-041', '每2个月', '刘工', 'planned'],
  ['30', '13:30', '市第一人民医院', '手术室', '高频电刀', 'DD-2022-051', '每季度', '陈工', 'planned'],
].map((item, index) => ({
  id: `plan-${index + 1}`,
  date: `2026-07-${item[0]}`,
  time: item[1],
  hospital: item[2],
  location: item[3],
  device: item[4],
  deviceNo: item[5],
  cycle: item[6],
  engineer: item[7],
  status: item[8] as PlanStatus,
}))

const statusMeta: Record<PlanStatus, { label: string; dot: string; pill: string; strip: string }> = {
  planned: { label: '待执行', dot: 'bg-blue-500', pill: 'bg-blue-50 text-blue-700', strip: 'border-blue-300 bg-blue-50 text-blue-800' },
  due: { label: '临近到期', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700', strip: 'border-amber-300 bg-amber-50 text-amber-800' },
  completed: { label: '已完成', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700', strip: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  overdue: { label: '已逾期', dot: 'bg-red-500', pill: 'bg-red-50 text-red-700', strip: 'border-red-300 bg-red-50 text-red-800' },
}

const weekNames = ['一', '二', '三', '四', '五', '六', '日']

function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function addDays(date: Date, count: number) {
  const result = new Date(date)
  result.setDate(result.getDate() + count)
  return result
}

function weekStart(date: Date) {
  return addDays(date, -(date.getDay() || 7) + 1)
}

export default function MaintenanceCalendar() {
  const [view, setView] = useState<CalendarView>('month')
  const [cursor, setCursor] = useState(new Date(2026, 6, 12))
  const [selectedDate, setSelectedDate] = useState('2026-07-12')
  const [hospital, setHospital] = useState(hospitals[0])
  const [engineer, setEngineer] = useState(engineers[0])
  const [plans, setPlans] = useState(initialPlans)
  const [modalOpen, setModalOpen] = useState(false)
  const [notice, setNotice] = useState('')

  const filtered = useMemo(() => plans.filter((plan) =>
    (hospital === hospitals[0] || plan.hospital === hospital) &&
    (engineer === engineers[0] || plan.engineer === engineer)
  ), [engineer, hospital, plans])

  const byDate = useMemo(() => {
    const result = new Map<string, MaintenancePlan[]>()
    filtered.forEach((plan) => result.set(plan.date, [...(result.get(plan.date) ?? []), plan]))
    return result
  }, [filtered])

  const monthDays = useMemo(() => {
    const start = weekStart(new Date(cursor.getFullYear(), cursor.getMonth(), 1))
    return Array.from({ length: 42 }, (_, index) => addDays(start, index))
  }, [cursor])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart(cursor), index)), [cursor])
  const visibleDays = view === 'month' ? monthDays : weekDays
  const dayPlans = byDate.get(selectedDate) ?? []
  const currentMonth = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
  const monthPlans = filtered.filter((plan) => plan.date.startsWith(currentMonth))

  const flash = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(''), 2600)
  }

  const move = (amount: number) => {
    const next = new Date(cursor)
    view === 'month' ? next.setMonth(next.getMonth() + amount) : next.setDate(next.getDate() + amount * 7)
    setCursor(next)
  }

  return (
    <div className="min-w-[1080px] bg-slate-50 p-6">
      {notice && <div className="fixed right-8 top-20 z-50 flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm text-white shadow-xl"><Check className="h-4 w-4 text-emerald-400" />{notice}</div>}

      <header className="mb-5 flex items-end justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-emerald-700"><CalendarDays className="h-4 w-4" />服务运营中心</div>
          <h1 className="text-2xl font-bold text-slate-900">设备维保计划</h1>
          <p className="mt-1 text-sm text-slate-500">按医疗机构与设备统筹周期性保养，计划到期自动提醒</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"><Plus className="h-4 w-4" />新建维保规则</button>
      </header>

      <div className="mb-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="mr-2 flex rounded-lg bg-slate-100 p-1">
            {(['month', 'week'] as const).map((item) => <button key={item} onClick={() => setView(item)} className={`rounded-md px-4 py-1.5 text-sm font-medium ${view === item ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{item === 'month' ? '月' : '周'}</button>)}
          </div>
          <button onClick={() => { setCursor(new Date(2026, 6, 12)); setSelectedDate('2026-07-12') }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">今天</button>
          <button onClick={() => move(-1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => move(1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><ChevronRight className="h-5 w-5" /></button>
          <b className="ml-1 min-w-36 text-lg text-slate-900">{view === 'month' ? `${cursor.getFullYear()}年${cursor.getMonth() + 1}月` : `${weekDays[0].getMonth() + 1}月${weekDays[0].getDate()}日—${weekDays[6].getMonth() + 1}月${weekDays[6].getDate()}日`}</b>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-3 text-xs text-slate-500 2xl:flex">{(Object.keys(statusMeta) as PlanStatus[]).map((status) => <span key={status} className="flex items-center gap-1.5"><i className={`h-2 w-2 rounded-full ${statusMeta[status].dot}`} />{statusMeta[status].label}</span>)}</div>
          <Filter className="h-4 w-4 text-slate-400" />
          <select value={hospital} onChange={(event) => setHospital(event.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">{hospitals.map((item) => <option key={item}>{item}</option>)}</select>
          <select value={engineer} onChange={(event) => setEngineer(event.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">{engineers.map((item) => <option key={item}>{item}</option>)}</select>
        </div>
      </div>

      <div className="mb-3 flex justify-between px-1 text-sm text-slate-500">
        <span>本月 <b className="text-slate-800">{monthPlans.length}</b> 项 · 已完成 <b className="text-emerald-700">{monthPlans.filter((plan) => plan.status === 'completed').length}</b> · 逾期 <b className="text-red-600">{monthPlans.filter((plan) => plan.status === 'overdue').length}</b></span>
        <span>计划由设备维保规则自动生成，可人工调整</span>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_330px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <section className="border-r border-slate-200">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">{weekNames.map((name) => <div key={name} className="py-3 text-center text-xs font-semibold text-slate-500">周{name}</div>)}</div>
          <div className={`grid grid-cols-7 ${view === 'month' ? 'grid-rows-6' : 'grid-rows-1'}`}>
            {visibleDays.map((date) => {
              const key = dateKey(date)
              const items = byDate.get(key) ?? []
              return <button key={key} onClick={() => setSelectedDate(key)} className={`border-b border-r border-slate-100 p-2 text-left hover:bg-slate-50 ${view === 'month' ? 'min-h-[112px]' : 'min-h-[672px]'} ${selectedDate === key ? 'bg-emerald-50/60 ring-2 ring-inset ring-emerald-500' : ''}`}>
                <div className="mb-2 flex items-center justify-between"><span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${key === '2026-07-12' ? 'bg-emerald-600 text-white' : date.getMonth() === cursor.getMonth() || view === 'week' ? 'text-slate-700' : 'text-slate-300'}`}>{date.getDate()}</span>{items.length > 2 && <span className="text-[11px] text-slate-400">{items.length}项</span>}</div>
                <div className="space-y-1.5">{items.slice(0, view === 'week' ? 8 : 2).map((plan) => <div key={plan.id} className={`truncate rounded-md border-l-2 px-2 py-1.5 text-[11px] font-medium ${statusMeta[plan.status].strip}`}>{view === 'week' && `${plan.time} `}{plan.hospital.replace('人民医院', '医院')} · {plan.device}</div>)}{view === 'month' && items.length > 2 && <div className="px-1 text-[11px] font-medium text-slate-500">还有 {items.length - 2} 项</div>}</div>
              </button>
            })}
          </div>
        </section>

        <aside className="flex min-h-[710px] flex-col">
          <div className="border-b border-slate-200 p-5">
            <div className="flex items-start justify-between"><div><h2 className="font-bold text-slate-900">{new Date(`${selectedDate}T00:00:00`).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'short' })}</h2><p className="mt-1 text-xs text-slate-500">{dayPlans.length ? `共 ${dayPlans.length} 项保养任务` : '当天暂无保养任务'}</p></div><button className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><Settings2 className="h-4 w-4" /></button></div>
            {!!dayPlans.length && <button onClick={() => flash(`已为 ${dayPlans.length} 项任务生成维保工单`)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"><Users className="h-4 w-4" />批量生成工单</button>}
          </div>
          <div className="flex-1 divide-y divide-slate-100 overflow-auto">
            {dayPlans.map((plan) => <article key={plan.id} className="p-5 hover:bg-slate-50">
              <div className="mb-3 flex justify-between"><b className="flex items-center gap-2 text-sm text-slate-900"><Clock3 className="h-4 w-4 text-slate-400" />{plan.time}</b><span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${statusMeta[plan.status].pill}`}>{statusMeta[plan.status].label}</span></div>
              <h3 className="text-sm font-semibold text-slate-900">{plan.device}</h3><p className="mt-1 text-xs text-slate-500">{plan.hospital}</p>
              <div className="mt-3 space-y-2 text-xs text-slate-500"><div className="flex gap-2"><MapPin className="h-3.5 w-3.5" />{plan.location}</div><div className="flex gap-2"><Stethoscope className="h-3.5 w-3.5" />{plan.deviceNo} · {plan.cycle}</div></div>
              <div className="mt-4 flex items-center justify-between"><span className="flex items-center gap-2 text-xs font-medium text-slate-600"><i className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700">{plan.engineer[0]}</i>{plan.engineer}</span>{plan.status !== 'completed' ? <button onClick={() => setPlans((items) => items.map((item) => item.id === plan.id ? { ...item, status: 'completed' } : item))} className="text-xs font-semibold text-emerald-700">标记完成</button> : <span className="flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" />已保养</span>}</div>
            </article>)}
            {!dayPlans.length && <div className="flex h-full flex-col items-center justify-center p-8 text-center"><div className="mb-3 rounded-full bg-slate-100 p-4"><CalendarDays className="h-5 w-5 text-slate-400" /></div><p className="text-sm font-medium text-slate-700">当天没有维保安排</p><p className="mt-1 text-xs leading-5 text-slate-400">选择其他日期，或新建周期性维保规则</p></div>}
          </div>
        </aside>
      </div>

      {modalOpen && <RuleModal date={selectedDate} onClose={() => setModalOpen(false)} onCreate={(plan) => { setPlans((items) => [...items, plan]); setModalOpen(false); flash('维保规则已创建，未来计划已自动生成') }} />}
    </div>
  )
}

function RuleModal({ date, onClose, onCreate }: { date: string; onClose: () => void; onCreate: (plan: MaintenancePlan) => void }) {
  const [form, setForm] = useState({ hospital: hospitals[1], device: '64排螺旋CT', deviceNo: 'CT-2021-018', cycle: '每季度', date, engineer: '陈工' })
  const update = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }))
  const field = 'w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100'
  return <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/30 p-6 backdrop-blur-[1px]">
    <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
      <div className="flex justify-between border-b border-slate-200 p-6"><div><h2 className="text-lg font-bold text-slate-900">新建维保规则</h2><p className="mt-1 text-sm text-slate-500">系统将按周期自动生成未来 12 个月计划</p></div><button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
      <div className="grid grid-cols-2 gap-5 p-6">
        <label className="space-y-2 text-sm font-medium text-slate-700">医疗机构<select value={form.hospital} onChange={(e) => update('hospital', e.target.value)} className={field}>{hospitals.slice(1).map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="space-y-2 text-sm font-medium text-slate-700">设备名称<input value={form.device} onChange={(e) => update('device', e.target.value)} className={field} /></label>
        <label className="space-y-2 text-sm font-medium text-slate-700">设备编号<input value={form.deviceNo} onChange={(e) => update('deviceNo', e.target.value)} className={field} /></label>
        <label className="space-y-2 text-sm font-medium text-slate-700">维保周期<select value={form.cycle} onChange={(e) => update('cycle', e.target.value)} className={field}>{['每月', '每2个月', '每季度', '每半年'].map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="space-y-2 text-sm font-medium text-slate-700">首次保养日期<input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} className={field} /></label>
        <label className="space-y-2 text-sm font-medium text-slate-700">默认负责人<select value={form.engineer} onChange={(e) => update('engineer', e.target.value)} className={field}>{engineers.slice(1).map((item) => <option key={item}>{item}</option>)}</select></label>
      </div>
      <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4"><button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-600">取消</button><button onClick={() => onCreate({ id: `plan-${Date.now()}`, date: form.date, time: '09:00', hospital: form.hospital, location: '待设置安装位置', device: form.device, deviceNo: form.deviceNo, cycle: form.cycle, engineer: form.engineer, status: 'planned' })} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white">创建并生成计划</button></div>
    </div>
  </div>
}
